import { PropsWithRef } from 'react';
import { InputFileProps } from './types';
import { InputFile as JuicyInput } from '@vtbhome/juicyfront';
import { FilesList, StyledFormGroup } from './input-file.styles';
import { IFileData } from '@vtbhome/juicyfront/types';
import { DEFAULT_INPUT_FILE_VALUE } from './constants';
import { File } from '../file/file';
import { UploadedFile } from '@shared/types/input-file';
import { ErrorMessageInput } from '../error-message-input/error-message-input';

export const InputFile = ({
    error,
    label,
    accept,
    hint,
    name,
    value = DEFAULT_INPUT_FILE_VALUE,
    dimension = 'l',
    placeholder,
    required,
    count,
    disabled,
    onChange,
    onChangeLocalFiles,
    onChangeUploadedFiles,
}: PropsWithRef<InputFileProps>) => {
    const isPossibleLoadMore = count ? value.localFiles.length + value.uploadedFiles.length < count : true;

    const handleRemoveUploadedFile = (file: UploadedFile) => () => {
        const newValue = { ...value };
        newValue.uploadedFiles = value.uploadedFiles.filter((uploadedFile) => uploadedFile.url !== file.url);
        if (onChangeUploadedFiles) {
            onChangeUploadedFiles(newValue);
        }
        if (onChange) {
            onChange(newValue);
        }
    };

    const handleRemoveLocalFile = (file: IFileData) => () => {
        const newValue = { ...value };
        newValue.localFiles = value.localFiles.filter((localFile) => localFile.file !== file.file);
        if (onChangeLocalFiles) {
            onChangeLocalFiles(newValue);
        }
        if (onChange) {
            onChange(newValue);
        }
    };

    const handleChangeLocalFiles = (files: IFileData[]) => {
        const newValue = { ...value };
        newValue.localFiles = files;
        const uploadedCount = value.uploadedFiles.length;
        const localCount = files.length;

        if (count) {
            const exceed = uploadedCount + localCount - count;
            newValue.localFiles = exceed > 0 ? newValue.localFiles.slice(0, -exceed) : newValue.localFiles;
        }
        if (onChangeLocalFiles) {
            onChangeLocalFiles(newValue);
        }
        if (onChange) {
            onChange(newValue);
        }
    };

    return (
        <StyledFormGroup
            label={label}
            required={required}
            $error={error}
            errorMessage={<ErrorMessageInput error={error} hint={hint} />}>
            <FilesList>
                {isPossibleLoadMore && (
                    <JuicyInput
                        buttonType='light'
                        placeholder={placeholder}
                        name={name}
                        showChips={false}
                        files={value.localFiles}
                        required={required}
                        accept={accept}
                        disabled={disabled}
                        setFile={handleChangeLocalFiles}
                    />
                )}
                {value.uploadedFiles.map((file) => (
                    <File
                        key={file.url}
                        uploadedFile={file}
                        dimension={dimension}
                        error={file.error}
                        isLoading={file.isLoading}
                        disabled={file.disabled}
                        onRemove={handleRemoveUploadedFile(file)}
                    />
                ))}
                {value.localFiles.map((file) => (
                    <File
                        key={`${file.file.name}${file.base64.slice(0, 20)}`}
                        localFile={file}
                        dimension={dimension}
                        error={file.error}
                        isLoading={file.isLoading}
                        disabled={file.disabled}
                        onRemove={handleRemoveLocalFile(file)}
                    />
                ))}
            </FilesList>
        </StyledFormGroup>
    );
};

InputFile.displayName = 'InputFile';
